import { Component, Input, SimpleChanges } from '@angular/core';

@Component({
    selector: 'mh-core-lib-gauge',
    templateUrl: './gauge.component.html',
    styleUrls: ['./gauge.component.scss'],
    standalone: false
})
export class GaugeComponent {
  @Input() value: number = 0;
  @Input() max: number = 100;
  @Input() min: number = -100;
  @Input() label: string = '';
  @Input() size: number = 200;
  @Input() thickness: number = 30;
  @Input() backgroundColor: string = '#e0e0e0';
  @Input() foregroundColor: string = 'red';
  @Input() arrowPosition: 'start' | 'end' | 'value' | number = 'value';
  // Rangos exactos para las secciones
  @Input() rangeSections: { min: number, max: number, color: string }[] = [
    { min: -100, max: 20, color: '#ff2020' }, // rojo
    { min: 20, max: 35, color: '#ff8020' },   // naranja
    { min: 35, max: 50, color: '#ffd748' },   // amarillo
    { min: 50, max: 65, color: '#96bf41' },   // verde claro
    { min: 65, max: 100, color: '#2ea83a' }   // verde oscuro
  ];

  radius: number = 0;
  circumference: number = 0;
  dashOffset: number = 0;
  viewBox: string = '';
  center: number = 0;
  
  arrowX: number = 0;
  arrowY: number = 0;
  arrowAngle: number = 0;
  Math: any = Math;

  

  ngOnInit() {
    this.initializeGauge();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateGauge();
  }

  private initializeGauge() {
    this.center = this.size / 2;
    this.radius = (this.size - this.thickness) / 2;
    this.circumference = this.radius * Math.PI;
    this.viewBox = `0 0 ${this.size} ${this.size}`;
    this.updateGauge();
  }

  private updateGauge() {
    const range = this.max - this.min; // Debe ser 200 cuando min=-100 y max=100
    
    // Normalizar el valor actual al rango 0-1 considerando el rango completo
    let valuePercentage = ((this.value - this.min) / range);
    valuePercentage = Math.min(1, Math.max(0, valuePercentage)); // Limitar entre 0 y 1
    
    this.dashOffset = this.arcLength * (1 - valuePercentage);
    
    // Determinar la posición de la flecha según arrowPosition
    let arrowPositionPercentage: number;
    
    if (this.arrowPosition === 'start') {
      arrowPositionPercentage = 0;
    } else if (this.arrowPosition === 'end') {
      arrowPositionPercentage = 1;
    } else if (this.arrowPosition === 'value') {
      arrowPositionPercentage = valuePercentage;
    } else if (typeof this.arrowPosition === 'number') {
      const normalizedPosition = Math.max(this.min, Math.min(this.max, this.arrowPosition));
      arrowPositionPercentage = ((normalizedPosition - this.min) / range);
    } else {
      arrowPositionPercentage = valuePercentage;
    }
    
    // Ajustar el ángulo para que 0 esté en la parte superior (90 grados)
    // El arco va desde -180 grados (izquierda) hasta 0 grados (derecha)
    // Cuando el valor es el mínimo, el ángulo debe ser -180 grados (π radianes)
    // Cuando el valor es el máximo, el ángulo debe ser 0 grados (0 radianes)
    this.arrowAngle = -Math.PI + (arrowPositionPercentage * Math.PI);
    
    // Utilizar un radio ligeramente menor para la flecha
    const arrowRadius = this.radius;
    
    // Calcular la posición X e Y basada en el ángulo con alta precisión
    this.arrowX = this.center + (arrowRadius * Math.cos(this.arrowAngle));
    this.arrowY = this.center + (arrowRadius * Math.sin(this.arrowAngle));
  }

  // Método para determinar en qué sección se encuentra un valor dado
  getSection(value: number): string {
    // Asegurar que el valor está dentro del rango
    value = Math.max(this.min, Math.min(this.max, value));
    
    for (const section of this.rangeSections) {
      if (value >= section.min && value < section.max) {
        return section.color;
      }
    }
    
    // Si llegamos aquí, es el valor máximo exacto
    return this.rangeSections[this.rangeSections.length - 1].color;
  }

  getRotationStyle(): string {
    return `rotate(-90 ${this.center} ${this.center})`;
  }

  get arrowRotation(): number {
    const dx = this.arrowX - this.center;
    const dy = this.arrowY - this.center;    
    return Math.atan2(dy, dx) * 180 / Math.PI - 90;
  }

  get startAngle(): number {
    return (Math.PI * 1.2); // 216 grados (posición inicial inferior)
  }
  
  get endAngle(): number {
    return (Math.PI * 1.35); // 243 grados (posición inicial inferior)
  }
  
  // Longitud del arco calculada correctamente
  get arcLength(): number {
    return Math.PI * this.radius; // Longitud de medio círculo
  }

  // Método para obtener el valor para mostrar en el texto central
  getDisplayValue(): string {
    if(this.value === null || this.value === undefined) {
      return '-';
    }
    return this.value >= 0 ? '+' + this.value : this.value.toString();
  }

  // Método para calcular el path de un arco basado en los valores de inicio y fin
  arcPath(startValue: number, endValue: number): string {
    const range = this.max - this.min; // Rango total (200 cuando min=-100, max=100)
    
    // Asegurar que los valores estén dentro del rango
    startValue = Math.max(this.min, Math.min(this.max, startValue));
    endValue = Math.max(this.min, Math.min(this.max, endValue));
    
    // Normalizar los valores al rango 0-1 con alta precisión
    const startPercentage = (startValue - this.min) / range;
    const endPercentage = (endValue - this.min) / range;
    
    // Calcular los ángulos correspondientes (de -180° a 0°)
    // -180° corresponde al valor mínimo (izquierda) y 0° al valor máximo (derecha)
    const startAngle = -Math.PI + (startPercentage * Math.PI);
    const endAngle = -Math.PI + (endPercentage * Math.PI);
    
    // Calcular puntos inicial y final del arco con mayor precisión
    const startX = this.center + (this.radius * Math.cos(startAngle));
    const startY = this.center + (this.radius * Math.sin(startAngle));
    const endX = this.center + (this.radius * Math.cos(endAngle));
    const endY = this.center + (this.radius * Math.sin(endAngle));
    
    // Determinar si el arco es mayor a 180 grados (π radianes)
    const largeArcFlag = Math.abs(endAngle - startAngle) > Math.PI ? 1 : 0;
    
    // Construir el path del arco con máxima precisión
    return `M ${startX.toFixed(4)} ${startY.toFixed(4)} A ${this.radius.toFixed(4)} ${this.radius.toFixed(4)} 0 ${largeArcFlag} 1 ${endX.toFixed(4)} ${endY.toFixed(4)}`;
  }
}
